<template>
    <div v-if="loaded">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form ref="Form">
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidades"
                            @change="loadFrom('unidade')"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableInfraestrutura"
                            :options="form.infraestruturas"
                            @change="loadFrom('infraestrutura')"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :disabled="disableCentroCusto"
                            :options="form.centrosCusto"
                            @change="loadFrom('centroCusto')"
                            default-option-text="Todos"
                            label="Setor"
                            show-default-option
                            text-field="setorNome"
                            v-model="model.centroCustoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :repository="repositories.relatorioAltoCustoProcessoPaciente.material"
                            :request-query-string-params="autocompleteQueryStringParams.material"
                            :search-fields="autocompleteFields.material"
                            label="Medicamento"
                            text-field="descricao"
                            uppercase
                            v-model="model.material"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :repository="repositories.relatorioAltoCustoProcessoPaciente.paciente"
                            :request-query-string-params="autocompleteQueryStringParams.paciente"
                            :search-fields="autocompleteFields.paciente"
                            text-field="nome"
                            label="Paciente"
                            uppercase
                            v-model="model.paciente"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <template v-slot:trailing>
                <rkm-dropdown
                    class="mr-2"
                    color="primary"
                    cols="1"
                    menu-class="auto-scroll"
                    no-caret
                    small
                    title="Colunas"
                    v-rkm-tooltip.hover
                >
                    <template v-slot:button-content>
                        <rkm-icon icon="columns" />
                    </template>

                    <rkm-form-checkbox
                        :indeterminate="indeterminate"
                        @change="selectAll"
                        class="mx-2"
                        v-model="selectedAll"
                    >
                        Todas
                    </rkm-form-checkbox>

                    <rkm-form-checkbox-group
                        :options="optionalColumns"
                        class="mx-2"
                        stacked
                        text-field="label"
                        v-model="selectedColumns"
                        value-field="key"
                    />
                </rkm-dropdown>

                <rkm-button
                    :disabled="itens.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="fields"
                :items="itens"
                class="table-resultado"
            >
                <template #cell(pacienteProntuarioUnidade)="{ value }">
                    {{ value || '-' }}
                </template>

                <template v-slot:cell(dataPrimeiraRetirada)="{ item }">
                    <div
                        v-for="(material, i) in item.materiais"
                        :key="i"
                        :style="getStyleMedicamentoRow(material)"
                    >
                        <hr v-if="i > 0">
                        {{ material.dataPrimeiraRetirada }}
                    </div>
                </template>

                <template v-slot:cell(material)="{ item }">
                    <div
                        v-for="(material, i) in item.materiais"
                        :key="i"
                        :style="getStyleMedicamentoRow(material)"
                    >
                        <hr v-if="i > 0">
                        {{ material.materialDescricao }}
                    </div>
                </template>

                <template v-slot:cell(retiradas)="{ item }">
                    <div
                        v-for="(material, i) in item.materiais"
                        :key="i"
                    >
                        <hr v-if="i > 0">
                        <div
                            v-for="(retirada, j) in material.retiradas"
                            :key="j"
                        >
                            {{ retirada }}
                        </div>
                    </div>
                </template>
            </rkm-table>
        </rkm-area>
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import { repository, error, data, object, stringCase, file } from '@/mixins'

export default {
    computed: {
        autocompleteQueryStringParams() {

            const params = this.prepareData()

            return {
                material: { ...params },
                paciente: { ...params },
            }
        },
        autocompleteFields() {

            return {
                material: [
                    { key: 'material.codigo_material', searchable: true },
                    { key: 'material.descricao', searchable: true },
                ],
                paciente: [
                    { key: 'paciente.prontuario', searchable: true },
                    { key: 'prontuario_unidade.prontuario', searchable: true },
                    { key: 'cidadao.nome', searchable: true },
                    { key: 'cidadao.data_nascimento', searchable: true },
                ],
            }
        },
        optionalColumns() {

            return this.columns.filter(c => !(c.fixed || false))
        },
        fields() {

            return this.columns.filter(c => (c.fixed || false) || this.selectedColumns.includes(c.key))
        },
        disableInfraestrutura() {

            return this.model.unidadeId === null
        },
        disableCentroCusto() {

            return this.model.infraestruturaId === null
        },
    },
    created() {

        this.repositories = {
            relatorioAltoCustoProcessoPaciente: this.getConectaSusRepository('farmacia.relatorioAltoCustoProcessoPaciente'),
            impressao: this.getConectaSusRepository('impressao.farmacia'),
        }

        this.load()
    },
    data() {

        return {
            loaded: false,
            additionals: null,
            form: null,
            model: null,
            initials: {
                form: null,
                model: null,
            },
            selectedAll: true,
            indeterminate: false,
            selectedColumns: [
                'pacienteProntuario',
                'pacienteProntuarioUnidade',
            ],
            columns: [
                { key: 'pacienteNome', label: 'Paciente', sortable: true, fixed: true },
                { key: 'pacienteProntuario', label: 'Prontuário', sortable: true, class: 'col-min text-center' },
                { key: 'pacienteProntuarioUnidade', label: 'Prontuário da unidade', sortable: true, class: 'col-min text-center' },
                { key: 'dataPrimeiraRetirada', label: 'Data de início', class: 'col-min text-center', fixed: true },
                { key: 'material', label: 'Medicamento', fixed: true },
                { key: 'retiradas', label: 'Retiradas', class: 'col-min text-center', fixed: true },
            ],
            itens: [],
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.relatorioAltoCustoProcessoPaciente.list()
                .then(({ data }) => {

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.initials.form = this.clone(data.form)
                    this.initials.model = this.clone(data.model)

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadFrom(field) {

            this.clearFrom(field)

            this.setLoading()

            this.repositories.relatorioAltoCustoProcessoPaciente.list({ ...this.prepareData(), load_from: this.camelToSnake(field) })
                .then(({ data }) => {

                    if ('form' in data) {

                        Object.keys(data.form).forEach(key => this.$set(this.form, key, data.form[key]))
                    }

                    Object.keys(data.model).forEach(key => this.$set(this.model, key, data.model[key]))
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        clearFrom(field) {

            if (field != 'material') {

                field = `${field}Id`
            }

            const fields = [
                'unidadeId',
                'infraestruturaId',
                'centroCustoId',
                'material',
                'paciente',
            ]

            fields.slice(fields.indexOf(field) + 1, fields.length)
                .forEach(key => this.$set(this.model, key, null))
        },
        search() {

            this.setLoading()

            this.repositories.relatorioAltoCustoProcessoPaciente.index(this.prepareData())
                .then(({ data }) => {

                    this.itens = data.pacientes
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {

            this.form = this.clone(this.initials.form)
            this.model = this.clone(this.initials.model)

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {

            this.setLoading()

            this.repositories.impressao.relatorioAltoCustoProcessoPaciente(this.prepareDataWithColumns())
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        prepareDataWithColumns() {

            const data = this.prepareData()

            data.selectedColumns = this.fields.map(f => ({
                key: this.camelToSnake(f.key),
                label: f.label,
                class: f.class
            }))

            return data
        },
        prepareData() {

            const data = this.clone(this.model)

            this.createIdKeysToObject(data, ['material', 'paciente'])

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        selectAll(selected) {

            this.selectedColumns = selected ? this.optionalColumns.map(c => c.key) : []
        },
        getStyleMedicamentoRow(material) {

            return { 'line-height': material.retiradas.length * 1.5, 'white-space': 'nowrap' }
        }
    },
    mixins: [repository, error, data, object, stringCase, file],
    watch: {
        model: {
            deep: true,
            handler() {

                this.itens = []
            }
        },
        selectedColumns(newValue) {

            this.selectedAll = newValue.length > 0 && newValue.length == this.optionalColumns.length
            this.indeterminate = newValue.length > 0 && newValue.length != this.optionalColumns.length
        },
    }
}
</script>

<style scoped>
.dropdown >>> .auto-scroll {
    max-height: 400px;
    overflow-y: auto;
    width: 400px;
}
</style>